import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { httpOptions } from '../../util/http.util';

export enum PasswordStrength {
  WEAK = 'weak',
  MODERATE = 'moderate', 
  STRONG = 'strong'
}

export interface PasswordStrengthResult {
  strength: PasswordStrength;
  feedback: string[];
}

interface PasswordStrengthApiResponse {
  data: {
    valid: boolean;
    message: string;
    errors?: {
      password?: string[];
    };
    error_messages?: string[];
  };
}

@Injectable({
  providedIn: 'root'
})
export class PasswordStrengthService {
  private readonly BASE_URL = environment['base_url'];

  constructor(private http: HttpClient) {}

  /**
   * Check password strength by calling the API
   * @param password The password to check
   * @param context Optional context containing tokens for authentication
   */
  checkPasswordStrength(
    password: string,
    context?: { confirmationToken?: string; resetPasswordToken?: string }
  ): Observable<PasswordStrengthResult> {
    if (!password) {
      return of({
        strength: PasswordStrength.WEAK,
        feedback: []
      });
    }

    // Build request body
    const body: any = {
      password_strength: {
        password: password
      }
    };

    // Add tokens if provided
    if (context?.confirmationToken) {
      body.confirmation_token = context.confirmationToken;
    } else if (context?.resetPasswordToken) {
      body.reset_password_token = context.resetPasswordToken;
    }

    return this.http
      .post<PasswordStrengthApiResponse>(
        `${this.BASE_URL}/auth/password_strength`,
        body,
        httpOptions()
      )
      .pipe(
        map((response) => this.mapApiResponseToResult(response, password)),
        catchError((error) => {
          console.error('Error checking password strength:', error);
          // Return a neutral result on API error to not block the user
          return of({
            strength: PasswordStrength.MODERATE,
            feedback: ['Unable to check password strength. Please ensure your password is secure.']
          });
        })
      );
  }

  private mapApiResponseToResult(
    response: PasswordStrengthApiResponse,
    password: string
  ): PasswordStrengthResult {
    const data = response.data;
    
    if (data.valid) {
      return {
        strength: PasswordStrength.STRONG,
        feedback: ['Strong password!']
      };
    }

    // Extract feedback from API response
    const feedback: string[] = [];
    
    if (data.error_messages && data.error_messages.length > 0) {
      feedback.push(...data.error_messages);
    } else if (data.errors?.password) {
      feedback.push(...data.errors.password);
    } else {
      feedback.push(data.message || 'Password is too weak');
    }

    // Determine strength based on feedback
    const strength = this.determineStrengthFromFeedback(password, feedback);
    
    return {
      strength,
      feedback
    };
  }

  private determineStrengthFromFeedback(
    password: string,
    feedback: string[]
  ): PasswordStrength {
    // If password is very short or has major issues, it's weak
    if (password.length < 8 || feedback.some(f => f.toLowerCase().includes('too weak'))) {
      return PasswordStrength.WEAK;
    }
    
    // Otherwise, it's moderate (since the API said it's not valid)
    return PasswordStrength.MODERATE;
  }

  // Get the main guidance message
  getGuidanceMessage(): string {
    return `To create a strong password:
• We recommend using a password manager to generate a strong password
• Use 12 characters
• Include uppercase, lowercase, numbers & symbols
• Avoid using common words or personal information`;
  }
} 